import { Settings, PanelRightClose, Crop, Waves, TypeOutline, ArrowDown01 } from 'lucide-react';
import { BackgroundSelector } from './BackgroundSelector.tsx';
import { BackgroundConfig, TextColorConfig } from '../types';
import { StageButtonRow } from './StageButton.tsx';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  jitterFactor : number;
  snapValue: number;
  stageSwitchTime : number;
  totalNumberOfStages : number;
  currentBackground: BackgroundConfig;
  currentTextColor: TextColorConfig;
  currentStage: number;
  onBackgroundChange: (background: BackgroundConfig) => void;
  onTextColorChange: (textColor : TextColorConfig) => void;
  onSnapValueChange: (snapValue: number) => void;
  onAntiBurnJitterChange: (jitterFactor : number) => void;
  onStageSettingsChange: (noStages : number, switchTime : number) => void;
  onStageChange: (stageNo: number) => void;
}

function getSecondaryColor(color :string) : string {
  switch (color) {
    case "#fff":
      return "#ccc";
    default:
      return "#333";
  }
}

function getStageButtonRowTable(index : number, totalNumberOfStages : number) : boolean[] {
  if (index) {
    let arr = new Array(totalNumberOfStages).fill(false);
    arr[index-1] = true;
    return arr;
  } else {
    return new Array(totalNumberOfStages).fill(true);
  }
}

export function SettingsPanel({
  isOpen,
  onClose,
  snapValue,
  stageSwitchTime,
  totalNumberOfStages,
  jitterFactor,
  currentBackground,
  currentTextColor,
  currentStage,
  onBackgroundChange,
  onTextColorChange,
  onSnapValueChange,
  onAntiBurnJitterChange,
  onStageSettingsChange,
  onStageChange
}: SettingsPanelProps) {
  return (
      <div className="settings-panel" style={{right: isOpen ? "0" : "-38rem"}}>
        {/* Header */}
        <div className="settings-header">
          <div className="settings-title-container">
            <Settings size={20} />
            <h2 className="settings-title">Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="settings-close-button"
          >
            <PanelRightClose size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
            <BackgroundSelector
              currentBackground={currentBackground}
              onBackgroundChange={onBackgroundChange}
            />
            <br/>
            <div className='settings-section'>
            <div className="background-section-title">
              <TypeOutline size={16} />
              <h2>Text Appearance</h2>
            </div>
            <div className="color-grid">
            {["#fff", "#000", "#1a2235"].map((color) => (
              <button
                key={color}
                onClick={() => onTextColorChange({ color: color, secondaryColor: getSecondaryColor(color) })}
                className={`color-button ${ currentTextColor.color === color ? 'active' : '' }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          </div>
            <div className="snap-section-title">
              <Crop size={16} />
              <h2>Snapping</h2>
            </div>
            <select style={{padding: "5px"}} value={snapValue} onChange={(event) => onSnapValueChange(Number.parseInt(event.target.value))} id="snap-values">
              <option value="1">Off</option>
              <option value="8">8</option>
              <option value="16">16</option>
              <option value="32">32</option>
              <option value="64">64</option>
            </select>
            <br/><br/>
            <div className="snap-section-title">
              <Waves size={16} />
              <h2>Anti-Burn Jitter</h2>
            </div>
            <select style={{padding: "5px"}} value={jitterFactor} onChange={(event) => onAntiBurnJitterChange(Number.parseInt(event.target.value))} id="anti-jitter-values">
              <option value="0">Off</option>
              <option value="4">Low</option>
              <option value="8">Medium</option>
              <option value="16">High</option>
              <option value="64">Ridiculous</option>
            </select>
            <br/><br/>
            <div className="snap-section-title">
              <ArrowDown01 size={16} />
              <h2>Stages</h2>
            </div>
            Switch stages every
            <select style={{padding: "5px"}} value={stageSwitchTime} onChange={(event) => {onStageSettingsChange(totalNumberOfStages, Number.parseInt(event.target.value))}} id="stage-change-time">
              <option value="3000">3s</option>
              <option value="5000">5s</option>
              <option value="10000">10s</option>
              <option value="30000">30s</option>
              <option value="0">Off</option>
            </select> 
            .
            <br/>
            There are total number of 
            <select style={{padding: "5px"}} value={totalNumberOfStages} onChange={(event) => {onStageSettingsChange(Number.parseInt(event.target.value), stageSwitchTime)}} id="no-of-stages">
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
            </select>
            stage(s).

            <StageButtonRow count={totalNumberOfStages} active={getStageButtonRowTable(currentStage,totalNumberOfStages)} onActivate={(i) => {onStageChange(i+1)}} />
            <button onClick={() => {onStageChange(0)}}>Omnipresent</button>
        </div>
      </div>
  );
}